import { useCallback, useEffect, useState } from "react";
import { apiFetch, type ApiErrorInfo, responseSupport } from "@/lib/api";
import type { ValuationStatus, ValuationSummary } from "@/features/valuation/valuation";

interface UseValuationSummaryResult {
  summary: ValuationSummary | null;
  loading: boolean;
  error: ApiErrorInfo | null;
  refresh: () => void;
}

function boundedString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed.slice(0, 160) : null;
}

function boundedNumber(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
}

function status(value: unknown, walletsNotValued: number): ValuationStatus {
  const raw = typeof value === "string" ? value.toUpperCase() : "";
  if (raw === "OK") {
    return walletsNotValued > 0 ? "PARTIAL" : "VALUED";
  }
  return raw === "VALUED"
    || raw === "PARTIAL"
    || raw === "UNVALUED"
    || raw === "STALE"
    || raw === "UNAVAILABLE"
    ? raw
    : "UNKNOWN";
}

function mapSummary(raw: Record<string, unknown>): ValuationSummary {
  const valuedWalletCount = boundedNumber(raw.walletsValued);
  const unvaluedWalletCount = boundedNumber(raw.walletsNotValued);
  const unvaluedPositions = raw.unvaluedPositions && typeof raw.unvaluedPositions === "object"
    ? raw.unvaluedPositions as Record<string, unknown>
    : {};
  return {
    valuedTotalUsd: boundedString(raw.valuedTotal),
    referenceTotalUsd: boundedString(raw.referenceTotal),
    valuedWalletCount,
    unvaluedWalletCount,
    staleWalletCount: boundedNumber(unvaluedPositions.STALE_PRICE),
    asOf: boundedString(raw.asOf),
    status: status(raw.status, unvaluedWalletCount),
  };
}

export function useValuationSummary(enabled = true): UseValuationSummaryResult {
  const [summary, setSummary] = useState<ValuationSummary | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<ApiErrorInfo | null>(null);

  const load = useCallback(async () => {
    if (!enabled) {
      setSummary(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch("/v1/valuations/summary", {}, "valuation-summary");
      const data = await res.json().catch(() => null);
      const body = data && typeof data === "object" ? data as Record<string, unknown> : {};
      if (!res.ok) {
        setSummary(null);
        setError({
          reasonCode: boundedString(body.reasonCode ?? body.reason_code ?? body.error) ?? `HTTP_${res.status}`,
          ...responseSupport(res, body),
        });
        return;
      }
      setSummary(mapSummary(body));
    } catch {
      setSummary(null);
      setError({ reasonCode: "NETWORK_ERROR" });
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => { load(); }, [load]);

  return { summary, loading, error, refresh: load };
}
